import { Divider, Flex, Skeleton, Stack } from '@mantine/core';
import { PlainButton, Text, Title } from '@repo/foundations';
import { isNil, size } from 'lodash-es';
import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import { AuthCard } from './AuthCard';
import { AuthLayoutBase } from './AuthLayouts/AuthLayoutBase';
import { AuthStepEmailComponent } from './AuthStepEmailComponent';
import { AuthStepSsoComponent } from './AuthStepSsoComponent';
import { AuthStepStartContext } from './AuthStepStartContext';
import { AuthTermsAndPolicy } from './AuthTermsAndPolicy';

export function AuthStepSignup() {
	const context = useContext(AuthStepStartContext);
	if (!context) {
		throw new Error(
			'AuthStepSignup must be used within a AuthStepStartContextProvider'
		);
	}
	const navigate = useNavigate();
	const { setError, setEmail, resetSSO, forceSSO, supportedMethods } = context;

	const handleSwitchToLogin = useCallback(() => {
		setError(null);
		navigate('/auth');
	}, [navigate, setError]);

	const handleBackToRegister = useCallback(() => {
		setError(null);
		setEmail('');
		resetSSO();
	}, [setError, setEmail, resetSSO]);

	return (
		<AuthLayoutBase>
			<Flex direction="column" gap="xl">
				<AuthCard>
					<Title order={1}>Sign up for Secoda</Title>
					<Stack mt="xl" spacing={0}>
						{isNil(supportedMethods) ? (
							<Stack spacing={'xs'}>
								<Skeleton height={36} radius="sm" />
								<Skeleton height={36} radius="sm" />
								<Skeleton height={36} radius="sm" />
								<Skeleton height={36} radius="sm" />
								<Skeleton height={36} radius="sm" />
							</Stack>
						) : (
							<>
								{supportedMethods?.includes('email') && (
									<>
										<AuthStepEmailComponent cta="Sign up with email" />
										{size(supportedMethods) > 1 && (
											<Divider
												hidden={!!forceSSO}
												my="md"
												label={
													<Text color="text/secondary/default" size="sm">
														or
													</Text>
												}
												labelPosition="center"
											/>
										)}
									</>
								)}
								<AuthStepSsoComponent
									supportedMethods={supportedMethods}
									buttonPrefix="Sign up"
								/>
							</>
						)}
						<Stack spacing="xl" mt="xl">
							<AuthTermsAndPolicy />
							{forceSSO ? (
								<PlainButton onClick={handleBackToRegister} size="md">
									Back to signup
								</PlainButton>
							) : (
								<Flex gap="xs" justify="center">
									<Text size="sm" color="text/secondary/default">
										Already have an account?
									</Text>
									<PlainButton onClick={handleSwitchToLogin} size="sm">
										Log in
									</PlainButton>
								</Flex>
							)}
						</Stack>
					</Stack>
				</AuthCard>
			</Flex>
		</AuthLayoutBase>
	);
}
